import { useCallback } from "react";

import { DataSourceNames, type SuggestionOption } from "../types";

interface SuggestionItemProps {
	suggestion: SuggestionOption;
	isHighlighted: boolean;
	onSelect: (suggestion: SuggestionOption) => void;
	onMouseEnter: () => void;
}

/**
 * SuggestionItem - Renders an individual suggestion item with proper accessibility
 */
const SuggestionItem: React.FC<SuggestionItemProps> = ({
	suggestion,
	isHighlighted,
	onSelect,
	onMouseEnter,
}) => {
	const handleClick = useCallback(() => {
		onSelect(suggestion);
	}, [onSelect, suggestion]);

	const handleMouseDown = useCallback((e: React.MouseEvent) => {
		// Prevent the input from losing focus when clicking on suggestions
		e.preventDefault();
	}, []);

	return (
		<div
			className={`px-3 py-2 cursor-pointer border-b border-elevated-2 last:border-b-0 flex justify-between items-center text-sm text-gray-50 hover:bg-elevated-2 focus:outline-none focus:bg-primary ${
				isHighlighted ? "bg-elevated-2" : ""
			}`}
			onClick={handleClick}
			onMouseDown={handleMouseDown}
			onMouseEnter={onMouseEnter}
			role="option"
			aria-selected={isHighlighted}
			aria-label={`${suggestion.label} from ${suggestion.source}`}
			data-testid={`suggestion-${suggestion.value}`}
		>
			<div className="text-sm text-gray-50">{suggestion.label}</div>
			<div className="text-xs text-gray-500 uppercase tracking-wide">
				{DataSourceNames[suggestion.source]}
			</div>
		</div>
	);
};

export default SuggestionItem;
